import { useState } from 'react';
import { Badge, Button, Card, FIELD_CLASS, Spinner, cx } from '@/components/ui';
import { FONT_LABELS, type FontChoice, readFontChoice, saveFontChoice } from '@/lib/font';
import { useChangePassword, useMe, useUpdateProfile } from './useAuth';
import { PushSettings } from './PushSettings';
import { NotificationChannels } from './NotificationChannels';
import { AUTH_ERROR_CODES } from './types';

/**
 * 내 정보. (3.6)
 *
 * 🔴 두 자리에서 같은 것을 쓴다
 *   일반 회원  상단 탭 '내 정보'
 *   관리자     '관리자' 탭 안의 하위 탭 '내 정보'
 *
 *   관리자에게는 상단 탭을 주지 않는다. 탭이 5개가 되어 폰에서 가로 스크롤이
 *   길어지기 때문이다. 대신 관리자 화면 안에 넣어 어느 쪽이든 갈 곳이 있게 한다.
 *
 *   컴포넌트를 하나로 두는 이유는, 둘로 두면 한쪽에만 항목을 추가하는 일이
 *   반드시 생기기 때문이다. 화면이 두 개인데 어느 쪽이 최신인지 알 수 없어진다.
 *
 * 여기서 못 하는 것
 *   아이디 변경   Redis 세션 인덱스의 키다. 바꾸면 자기 세션을 찾을 수 없다
 *   권한·상태 변경 관리자만 할 수 있다. 서버가 그 필드를 아예 읽지 않는다
 */
export function MyInfoPanel() {
  const { me, isLoading } = useMe();

  if (isLoading) {
    return <Spinner />;
  }
  if (!me) {
    // ProtectedRoute 가 이미 걸러낸다. 여기까지 오면 세션이 방금 끊긴 것이다.
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      <ProfileForm
        key={me.id}
        displayName={me.displayName}
        email={me.email ?? ''}
        loginId={me.loginId}
        admin={me.admin}
      />
      <PasswordForm />
      <PushSettings />
      {/* 켜둔 곳들을 관리한다. 이 브라우저의 구독 자체는 위 카드가 켜고 끈다 */}
      <NotificationChannels />
      <FontForm />
    </div>
  );
}

// ─────────────────────────────────────────────
// 표시 이름 · 이메일
// ─────────────────────────────────────────────

function ProfileForm({
  displayName: initialName,
  email: initialEmail,
  loginId,
  admin,
}: {
  displayName: string;
  email: string;
  loginId: string;
  admin: boolean;
}) {
  const [displayName, setDisplayName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);

  const mutation = useUpdateProfile();

  const 바뀐것없음 = displayName === initialName && email === initialEmail;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {loginId}
          {admin && <Badge tone="info">관리자</Badge>}
        </span>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs text-fg-muted">
          표시 이름
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            value={displayName}
            maxLength={50}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>

        <label className="text-xs text-fg-muted">
          이메일 (선택)
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
      </div>

      {/*
        아이디를 못 바꾸는 이유를 적어둔다.
        입력칸이 없는 것만 보면 "빠뜨렸나" 로 읽힌다.
      */}
      <p className="mt-2 text-xs text-fg-subtle">
        아이디는 바꿀 수 없습니다. 로그인 식별자이자 세션을 찾는 키입니다.
      </p>

      {mutation.error && <p className="mt-3 text-sm text-danger">{mutation.error.message}</p>}
      {mutation.isSuccess && !mutation.isPending && (
        <p className="mt-3 text-sm text-ok">저장했습니다.</p>
      )}

      <div className="mt-4">
        <Button
          disabled={mutation.isPending || !displayName.trim() || 바뀐것없음}
          onClick={() =>
            mutation.mutate({
              displayName: displayName.trim(),
              // 빈 문자열을 그대로 보내면 서버가 null 로 바꾸지만,
              // 여기서도 맞춰두면 "안 바뀌었다" 판정이 흔들리지 않는다.
              email: email.trim() === '' ? null : email.trim(),
            })
          }
        >
          저장
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// 비밀번호
// ─────────────────────────────────────────────

/** 계정 생성·관리자 재설정과 같은 값이어야 한다. 어긋나면 경로에 따라 강도가 달라진다. */
const MIN_PASSWORD_LENGTH = 8;

function PasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const mutation = useChangePassword();

  const 짧다 = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH;
  const 확인불일치 = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const 보낼수있음 =
    currentPassword.length > 0 &&
    newPassword.length >= MIN_PASSWORD_LENGTH &&
    newPassword === confirmPassword;

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <Card title="비밀번호 변경">
      <div className="flex flex-col gap-3">
        <label className="text-xs text-fg-muted">
          현재 비밀번호
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
          />
        </label>

        <label className="text-xs text-fg-muted">
          새 비밀번호 ({MIN_PASSWORD_LENGTH}자 이상)
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
          />
        </label>

        {/*
          확인 입력을 두는 이유
            비밀번호 칸은 글자가 안 보인다. 오타가 나면 저장은 성공하는데
            다음 로그인에서 막히고, 그때는 무엇을 잘못 쳤는지 알 방법이 없다.
            관리자에게 재설정을 부탁하는 것 말고는 복구 수단이 없다.
        */}
        <label className="text-xs text-fg-muted">
          새 비밀번호 확인
          <input
            className={cx(FIELD_CLASS, 'mt-1')}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </label>
      </div>

      {짧다 && (
        <p className="mt-3 text-sm text-danger">
          비밀번호는 {MIN_PASSWORD_LENGTH}자 이상이어야 합니다.
        </p>
      )}
      {확인불일치 && <p className="mt-3 text-sm text-danger">새 비밀번호가 서로 다릅니다.</p>}

      {mutation.error && (
        <p className="mt-3 text-sm text-danger">
          {mutation.error.code === AUTH_ERROR_CODES.CURRENT_PASSWORD_MISMATCH
            ? '현재 비밀번호가 올바르지 않습니다.'
            : mutation.error.message}
        </p>
      )}

      {/*
        끊긴 기기 수를 말해준다.
        0 이면 "다른 기기에 로그인한 적이 없다" 는 뜻이라 굳이 숫자를 꺼내지 않는다.
      */}
      {mutation.isSuccess && !mutation.isPending && (
        <p className="mt-3 text-sm text-ok">
          비밀번호를 바꿨습니다.
          {mutation.data > 0
            ? ` 다른 기기 ${mutation.data}곳에서 로그아웃되었습니다.`
            : ' 이 기기는 그대로 로그인 상태입니다.'}
        </p>
      )}

      <div className="mt-4">
        <Button
          disabled={mutation.isPending || !보낼수있음}
          onClick={() =>
            mutation.mutate({ currentPassword, newPassword }, { onSuccess: reset })
          }
        >
          비밀번호 변경
        </Button>
      </div>

      <p className="mt-3 text-xs text-fg-subtle">
        바꾸면 다른 기기에서는 로그아웃됩니다. 지금 보고 있는 이 기기는 그대로입니다.
      </p>
    </Card>
  );
}

// ─────────────────────────────────────────────
// 글꼴
// ─────────────────────────────────────────────

function FontForm() {
  /*
    서버가 아니라 localStorage 에 있다. (lib/font.ts)
    그래서 useQuery 가 아니라 useState 로 읽는다 — 서버 상태가 아니다.
    테마 토글과 같은 성격이다. (CLAUDE.md 5.4절)
  */
  const [choice, setChoice] = useState<FontChoice>(() => readFontChoice());

  const pick = (next: FontChoice) => {
    setChoice(next);
    saveFontChoice(next);
  };

  return (
    <Card title="글꼴">
      <div className="flex flex-col gap-2">
        {(Object.keys(FONT_LABELS) as FontChoice[]).map((value) => {
          const selected = value === choice;

          return (
            <label
              key={value}
              className={cx(
                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                selected ? 'border-brand bg-brand-weak' : 'border-line hover:bg-surface-muted',
              )}
            >
              <input
                type="radio"
                name="font"
                className="mt-0.5 h-4 w-4"
                checked={selected}
                onChange={() => pick(value)}
              />
              <span>
                <span
                  className={cx(
                    'block text-sm',
                    selected ? 'font-semibold text-brand' : 'text-fg',
                  )}
                >
                  {FONT_LABELS[value].name}
                </span>
                <span className="mt-0.5 block text-xs text-fg-muted">
                  {FONT_LABELS[value].note}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {/*
        미리보기. 글꼴이 바뀌면 이 줄이 즉시 바뀐다.
        숫자를 넣은 이유는 이 앱에서 글꼴을 가장 많이 보는 곳이 표의 숫자이기 때문이다.
      */}
      <p className="tabular mt-4 rounded-lg bg-surface-muted p-3 text-sm text-fg">
        삼성전자 005930 · 1,234,567원 · +2.45%
      </p>

      <p className="mt-3 text-xs text-fg-subtle">
        이 설정은 이 기기에만 저장됩니다. 폰과 PC 를 따로 정할 수 있습니다.
      </p>
    </Card>
  );
}
